import React, { useState, useEffect } from 'react'
import Headers from '../components/Headers'
import Footer from '../components/Footer'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import api from '../api/api'
import { useDispatch, useSelector } from 'react-redux'
import { user_reset } from '../store/reducers/authReducer'
import { reset_count } from '../store/reducers/cardReducer'
import { Heart, LayoutDashboard, Lock, LogOut, MessageSquareText, ShoppingBag, User, Bell, LifeBuoy, Gift, RotateCcw, CalendarClock } from 'lucide-react'
import HeadersDashboard from '../components/HeadersDashboard'

const Dashboard = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { userInfo, profileImage } = useSelector((state) => state.auth)
    const [filterShow, setFilterShow] = useState(false)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [notificationCount, setNotificationCount] = useState(0)

    useEffect(() => {
        // Fetch notification count
        const fetchNotificationCount = async () => {
            if (!userInfo) return;
            
            try {
                const response = await api.get('/customer/notifications');
                if (response.data.success && response.data.notifications) {
                    const unread = response.data.notifications.filter(n => 
                        !n.readBy.some(read => read.userId === userInfo._id && read.userType === 'customers')
                    ).length;
                    setNotificationCount(unread);
                }
            } catch (error) {
                console.error('Error fetching notification count:', error);
            }
        };

        if (userInfo) {
            fetchNotificationCount();
            // Refresh every 30 seconds
            const interval = setInterval(fetchNotificationCount, 30000);
            return () => clearInterval(interval);
        }
    }, [userInfo])

    const handleLogout = async () => {
        try {
            const { data } = await api.get('/customer/logout')
            localStorage.removeItem('customerToken')
            dispatch(user_reset())
            dispatch(reset_count())
            navigate('/login')
        } catch (error) {
            console.log(error.response.data)
        }
    }

    // Shadcn-styled custom dialog
    const ConfirmDialog = () => {
        if (!showConfirmDialog) return null;

        return (
            <>
                {/* Overlay with animation */}
                <div 
                    className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
                    onClick={() => setShowConfirmDialog(false)}
                />
                
                {/* Dialog content with animations */}
                <div className="fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] rounded-md translate-y-[-50%] gap-4  bg-white border border-gray-200 p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
                    {/* Header */}
                    <div className="flex flex-col space-y-2 sm:text-left">
                        <h2 className="text-lg font-semibold text-foreground">
                            Are you sure you want to logout?
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            You will need to log in again to access your account.
                        </p>
                    </div>
                    
                    {/* Footer */}
                    <div className="flex items-center sm:flex-row justify-end sm:space-x-2">
                        <button
                            onClick={() => setShowConfirmDialog(false)}
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 border border-input bg-transparent hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2 sm:mt-0"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                setShowConfirmDialog(false)
                                handleLogout()
                            }}
                            className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2 ml-2"
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </>
        )
    }

    return (
        <div>
            <Headers />
            <div className=''>
                <div className='h-full mx-auto'>
                    <div className=' flex mx-auto relative md-lg:flex-col'>
                    <div className={`-left-[360px] w-[270px] md-lg:w-full h-[85vh] md-lg:h-[auto]   md-lg:ml-0 ml-4 md-lg:bg-transparent bg-gray-50`}>
                            {/* User profile card */}
                            <div className='px-10 md-lg:px-5 pt-8 md-lg:pt-4 pb-4 md-lg:pb-3 border-b border-gray-200 mb-2'>
                                <div className='flex items-center gap-3'>
                                    <div className='w-12 h-12 md-lg:w-9 md-lg:h-9 rounded-full overflow-hidden bg-gray-200 border-2 border-gray-300 flex items-center justify-center shrink-0'>
                                        {profileImage ? (
                                            <img src={profileImage} alt={userInfo?.name} className='w-full h-full object-cover' />
                                        ) : (
                                            <span className='text-lg md-lg:text-sm font-bold text-gray-600'>
                                                {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : <User size={20} />}
                                            </span>
                                        )}
                                    </div>
                                    <div className='min-w-0'>
                                        <p className='text-sm font-semibold text-gray-900 truncate'>{userInfo?.name}</p>
                                        <p className='text-xs text-gray-500 truncate'>{userInfo?.email}</p>
                                    </div>
                                </div>
                            </div>
                            <ul className='text-gray-700 p-10 md-lg:p-0 md-lg:px-5 flex flex-col md-lg:flex-row md-lg:flex-wrap gap-2 md-lg:gap-2  md-lg:gap-x-2 text-sm md-lg:mb-5'>
                                <li className='flex justify-start items-center gap-2 py-2 md-lg:px-3 md-lg:py-1.5 md-lg:bg-gray-100 rounded-full'>
                                    <LayoutDashboard size={16} className='shrink-0'/>
                                    <Link to='/dashboard' className='block'>Dashboard</Link>
                                </li>
                                <li className='flex justify-start items-center gap-2 py-2 md-lg:px-3 md-lg:py-1.5 md-lg:bg-gray-100 rounded-full'>
                                    <ShoppingBag size={16} className='shrink-0'/>
                                    <Link to='/dashboard/my-orders' className='block'>My Orders</Link>
                                </li>
                                <li className='flex justify-start items-center gap-2 py-2 md-lg:px-3 md-lg:py-1.5 md-lg:bg-gray-100 rounded-full'>
                                    <RotateCcw size={16} className='shrink-0'/>
                                    <Link to='/dashboard/my-returns' className='block'>My Returns</Link>
                                </li>
                                <li className='flex justify-start items-center gap-2 py-2 md-lg:px-3 md-lg:py-1.5 md-lg:bg-gray-100 rounded-full'>
                                    <CalendarClock size={16} className='shrink-0'/>
                                    <Link to='/dashboard/my-installments' className='block'>My Installments</Link>
                                </li>
                                <li className='flex justify-start items-center gap-2 py-2 md-lg:px-3 md-lg:py-1.5 md-lg:bg-gray-100 rounded-full'>
                                    <Heart size={16} className='shrink-0'/>
                                    <Link to='/dashboard/my-wishlist' className='block'>Wishlist</Link>
                                </li>
                                <li className='flex justify-start items-center gap-2 py-2 md-lg:px-3 md-lg:py-1.5 md-lg:bg-gray-100 rounded-full'>
                                    <MessageSquareText size={16} className='shrink-0'/>
                                    <Link to='/dashboard/chat' className='block'>Chat</Link>
                                </li>
                                <li className='flex justify-start items-center gap-2 py-2 md-lg:px-3 md-lg:py-1.5 md-lg:bg-gray-100 rounded-full'>
                                    <Bell size={16} className='shrink-0'/>
                                    <Link to='/dashboard/notifications' className='flex items-center gap-2'>
                                        <span>Notifications</span>
                                        {notificationCount > 0 && (
                                            <span className="bg-red-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                                {notificationCount > 9 ? '9+' : notificationCount}
                                            </span>
                                        )}
                                    </Link>
                                </li>
                                <li className='flex justify-start items-center gap-2 py-2 md-lg:px-3 md-lg:py-1.5 md-lg:bg-gray-100 rounded-full'>
                                    <Gift size={16} className='shrink-0'/>
                                    <Link to='/dashboard/referral' className='block'>Referrals</Link>
                                </li>
                                <li className='flex justify-start items-center gap-2 py-2 md-lg:px-3 md-lg:py-1.5 md-lg:bg-gray-100 rounded-full'>
                                    <LifeBuoy size={16} className='shrink-0'/>
                                    <Link to='/dashboard/support' className='block'>Help & Support</Link>
                                </li>
                                <li className='flex justify-start items-center gap-2 py-2 md-lg:px-3 md-lg:py-1.5 md-lg:bg-gray-100 rounded-full'>
                                    <User size={16} className='shrink-0'/>
                                    <Link to='/dashboard/account' className='block'>Account & Shipping</Link>
                                </li>
                                <li
                                    onClick={() => setShowConfirmDialog(true)}
                                    className='cursor-pointer flex justify-start items-center gap-2 py-2 md-lg:px-3 md-lg:py-1.5 md-lg:bg-gray-100 rounded-full'
                                >
                                    <LogOut size={16} className='shrink-0'/>
                                    <div className='block'>Logout</div>
                                </li>
                            </ul>
                        </div>
                        <div className='w-[calc(100%-270px)] md-lg:w-full'>
                            <div className='mx-4 md-lg:mx-0'>
                                <Outlet />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ConfirmDialog />
          
        </div>
    )
}

export default Dashboard